import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Trophy, TrendingUp, Calendar, MapPin, Activity, Users,
  Share2, MessageSquare, Plus, Clock, Award, BarChart2,
  ChevronDown, ChevronRight, Shield, Target, Zap, Star,
  ArrowLeft, Loader2, AlertTriangle, Heart, Eye,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { AZURE } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   Design Tokens — VUKZ mono-dark style
   ═══════════════════════════════════════════════════════════════ */
const T = {
  bg: '#f3f2ef',
  card: '#ffffff',
  text: '#111111',
  textSoft: '#6b7280',
  textMute: '#9ca3af',
  border: '#e5e7eb',
  accent: '#111111',
  accentSoft: '#f3f4f6',
  gold: '#b48c2c',
  success: '#16a34a',
  warning: '#f59e0b',
  error: '#dc2626',
  info: '#2563eb',
};

/* ═══════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════ */

const StatBox = ({ label, value, icon: Icon, highlight }) => (
  <div className={`rounded-xl border p-4 text-center transition-all hover:shadow-md ${
    highlight ? 'border-[#111111] bg-gray-50' : 'border-gray-100 bg-white'
  }`}>
    {Icon && <Icon size={14} className="mx-auto mb-1" style={{ color: T.textMute }} />}
    <div className="text-2xl font-black" style={{ color: T.text }}>{value}</div>
    <div className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: T.textSoft }}>{label}</div>
  </div>
);

const ProfileHeader = ({ player, onFollow }) => {
  const avatarUrl = player.photo_url || player.avatar_url;
  const age = player.birth_date
    ? Math.floor((Date.now() - new Date(player.birth_date).getTime()) / 31557600000)
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      {/* Cover — subtle gradient */}
      <div className="h-40 relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/diagonal-striped-brick.png')]" />
        {/* Position badge */}
        {player.position && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
            {player.position}
          </div>
        )}
      </div>

      <div className="px-6 pb-6 relative">
        <div className="flex flex-col md:flex-row gap-5 items-start md:items-end -mt-14 mb-4">
          {/* Avatar */}
          <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg bg-white overflow-hidden z-10 flex items-center justify-center flex-shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={player.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-black text-gray-300">
                {player.name?.substring(0, 2).toUpperCase() || '?'}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 pt-2">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl md:text-2xl font-black tracking-tight" style={{ color: T.text }}>
                {player.name}
              </h1>
              {player.nationality && (
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600 font-semibold">
                  {player.nationality}
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: T.textSoft }}>
              {player.current_team || player.team_name || 'Sem clube'}
              {age && ` • ${age} anos`}
              {player.height && ` • ${player.height}cm`}
            </p>

            {/* Quick stats row */}
            <div className="flex gap-6 mt-3 text-sm">
              <div className="text-center">
                <span className="block font-black text-base" style={{ color: T.text }}>
                  {player.total_goals ?? player.goals ?? 0}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: T.textSoft }}>Gols</span>
              </div>
              <div className="text-center">
                <span className="block font-black text-base" style={{ color: T.text }}>
                  {player.total_assists ?? player.assists ?? 0}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: T.textSoft }}>Assistências</span>
              </div>
              <div className="text-center">
                <span className="block font-black text-base" style={{ color: T.text }}>
                  {player.total_appearances ?? player.appearances ?? 0}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: T.textSoft }}>Jogos</span>
              </div>
              <div className="text-center">
                <span className="block font-black text-base" style={{ color: T.gold }}>
                  {typeof player.avg_rating === 'number' ? player.avg_rating.toFixed(1) : '-'}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: T.textSoft }}>Rating</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={onFollow}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: T.accent }}>
              <Plus size={14} /> Seguir
            </button>
            <button className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50">
              <MessageSquare size={16} style={{ color: T.textSoft }} />
            </button>
            <button className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50">
              <Share2 size={16} style={{ color: T.textSoft }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Radar Chart for player profile ────────────────────────── */
const PlayerRadar = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <h3 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: T.textSoft }}>
        <Target size={12} className="inline mr-1" /> Perfil Técnico
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={data} outerRadius={100}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: T.textSoft }} />
          <PolarRadiusAxis tick={false} domain={[0, 100]} />
          <Radar name="Player" dataKey="value" stroke={T.accent} fill={T.accent} fillOpacity={0.15} strokeWidth={2} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Season Stats Table ────────────────────────────────────── */
const SeasonTable = ({ seasons }) => {
  if (!seasons || seasons.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
          <Calendar size={12} className="inline mr-1" /> Estatísticas por Temporada
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-left">
              {['Competição', 'Temp.', 'J', 'Min', 'G', 'A', 'Fin', 'PD', 'Rating'].map(h => (
                <th key={h} className="px-3 py-2 font-bold uppercase tracking-wider" style={{ color: T.textMute, fontSize: '10px' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {seasons.map((s, i) => {
              const rating = typeof s.rating_medio === 'number' ? s.rating_medio.toFixed(1) : '-';
              return (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2.5 font-semibold" style={{ color: T.text }}>
                    {s.league_name || 'N/D'}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: T.textSoft }}>{s.season || '-'}</td>
                  <td className="px-3 py-2.5 font-bold">{s.jogos || 0}</td>
                  <td className="px-3 py-2.5">{Number(s.minutos || 0).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 font-bold" style={{ color: T.success }}>{s.gols || 0}</td>
                  <td className="px-3 py-2.5 font-bold" style={{ color: T.info }}>{s.assistencias || 0}</td>
                  <td className="px-3 py-2.5">{s.finalizacoes || 0}</td>
                  <td className="px-3 py-2.5">{s.passes_decisivos || 0}</td>
                  <td className="px-3 py-2.5 font-black" style={{ color: parseFloat(rating) >= 7 ? T.gold : T.text }}>
                    {rating}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Evolution Chart ───────────────────────────────────────── */
const EvolutionChart = ({ seasons, metric, setMetric }) => {
  const metrics = [
    { id: 'gols', label: 'Gols', color: '#10b981' },
    { id: 'assistencias', label: 'Assist.', color: '#f59e0b' },
    { id: 'jogos', label: 'Jogos', color: '#3b82f6' },
    { id: 'rating_medio', label: 'Rating', color: '#ef4444' },
    { id: 'minutos', label: 'Minutos', color: '#8b5cf6' },
  ];
  const current = metrics.find(m => m.id === metric) || metrics[0];

  const chartData = (seasons || []).map(s => ({
    name: `${(s.league_name || '').substring(0, 15)} ${s.season || ''}`.trim(),
    value: Number(s[metric] || 0),
  }));

  if (chartData.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: T.textSoft }}>
          <TrendingUp size={12} className="inline mr-1" /> Evolução
        </h3>
        <div className="flex gap-1">
          {metrics.map(m => (
            <button key={m.id} onClick={() => setMetric(m.id)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${
                metric === m.id ? 'bg-[#111] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-10} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }} />
          <Line type="monotone" dataKey="value" stroke={current.color} strokeWidth={3}
            dot={{ r: 4, fill: current.color, strokeWidth: 2, stroke: '#fff' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main Page
   ═══════════════════════════════════════════════════════════════ */
const FootballProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('gols');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [playerRes, statsRes] = await Promise.all([
          AZURE.get(`/football/player/${id}`),
          AZURE.get(`/football/player/${id}/stats/summary`).catch(() => ({ data: null })),
        ]);

        const pData = playerRes.data?.success ? playerRes.data.data || playerRes.data : playerRes.data;
        setPlayer(pData);

        if (statsRes.data?.success) {
          setStats(statsRes.data);
        }
      } catch (err) {
        console.error('[FootballProfile] fetch error:', err);
        setError(err.response?.data?.error || err.message || 'Erro ao carregar perfil');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAll();
  }, [id]);

  // Build radar data from stats
  const radarData = useMemo(() => {
    if (!stats?.totals_overall) return [];
    const t = stats.totals_overall;
    const maxGames = Math.max(Number(t.jogos || 1), 1);
    return [
      { metric: 'Finalização', value: Math.min(((Number(t.finalizacoes || 0) / maxGames) * 20), 100) },
      { metric: 'Gols', value: Math.min(((Number(t.gols || 0) / maxGames) * 50), 100) },
      { metric: 'Assistência', value: Math.min(((Number(t.assistencias || 0) / maxGames) * 50), 100) },
      { metric: 'Passes Dec.', value: Math.min(((Number(t.passes_decisivos || 0) / maxGames) * 30), 100) },
      { metric: 'Rating', value: Math.min(((Number(t.rating_medio || 0)) * 10), 100) },
      { metric: 'Titular', value: Math.min(((Number(t.vezes_titular || 0) / maxGames) * 100), 100) },
    ];
  }, [stats]);

  const seasons = useMemo(() => {
    if (!stats?.by_competition) return [];
    return Array.isArray(stats.by_competition) ? stats.by_competition : [];
  }, [stats]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <Loader2 className="animate-spin" size={28} style={{ color: T.accent }} />
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} style={{ color: T.warning }} className="mx-auto mb-3" />
          <p className="text-sm font-semibold" style={{ color: T.text }}>{error || 'Jogador não encontrado'}</p>
          <button onClick={() => navigate(-1)}
            className="mt-3 px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: T.accent }}>
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const totals = stats?.totals_overall || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 space-y-6">

        {/* Back */}
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs font-bold hover:underline" style={{ color: T.textSoft }}>
          <ArrowLeft size={14} /> Voltar
        </button>

        {/* Profile Header */}
        <ProfileHeader player={player} onFollow={() => {}} />

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatBox label="Jogos" value={totals.jogos || 0} icon={Activity} />
          <StatBox label="Minutos" value={Number(totals.minutos || 0).toLocaleString('pt-BR')} icon={Clock} />
          <StatBox label="Gols" value={totals.gols || 0} icon={Target} highlight />
          <StatBox label="Assistências" value={totals.assistencias || 0} icon={Zap} />
          <StatBox label="Titular" value={totals.vezes_titular || 0} icon={Shield} />
          <StatBox label="Rating" value={typeof totals.rating_medio === 'number' ? totals.rating_medio.toFixed(1) : '-'} icon={Star} highlight />
        </div>

        {/* Middle row: Radar + Evolution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PlayerRadar data={radarData} />
          <EvolutionChart seasons={seasons} metric={metric} setMetric={setMetric} />
        </div>

        {/* Season Table */}
        <SeasonTable seasons={seasons} />
      </div>
    </div>
  );
};

export default FootballProfilePage;
